import { onMounted, computed, ref, readonly } from "vue";
import { useEventListener } from "@vueuse/core";
import { MACOS_SYMBOL_LAYER_KEYS } from "./macos-symbol-layer";
import { MACOS_UPPERCASE_LAYER_KEYS } from "./macos-uppercase-layer";
import { onUnmounted } from "vue";

const TIMEOUT = 500;
const MODIFIERS = [
    "Accel",
    "Alt",
    "Altgraph",
    "Capslock",
    "Control",
    "Fn",
    "Fnlock",
    "Hyper",
    "Meta",
    "Numlock",
    "Scrolllock",
    "Shift",
    "Super",
    "Symbol",
    "Symbollock",
];

const NON_TYPING_INPUT_TYPES = new Set([
    "checkbox",
    "radio",
    "button",
    "reset",
    "submit",
    "file",
]);

// Key which cannot be part of the string.
const SYNTHETIC_KEYNAMES: Record<string, string> = {
    " ": "Space",
    "+": "Plus",
};

const isMac = /Mac|iPod|iPhone|iPad/i.test(navigator.userAgent);

/**
 * Check whether a given element might accept input.
 * This should prevent a hotkey from being triggered.
 */
const isTypable = (element: HTMLElement) => {
    return (
        (element.tagName === "INPUT" &&
            !NON_TYPING_INPUT_TYPES.has((element as HTMLInputElement).type)) ||
        element.tagName === "TEXTAREA" ||
        element.tagName === "SELECT" ||
        element.tagName.startsWith("C-") ||
        element.isContentEditable
    );
};

const hotKeys = ref<
    Record<
        string,
        {
            handler: (event: KeyboardEvent) => void;
            explanation?: string;
        }
    >
>({});

export const useHotKeys = () => {
    /**
     * Add hotkey sequences.
     *
     * The sequence parameter is a string with the keycombination/sequence.
     * The string can use `+` to combine modifier keys with keys. A ` ` (space)
     * in the sequence means that it comes in sequence, after the previous keypress.
     *
     * For example: `Control+n r` responds to pressing alt and n at the same time and
     * then later r without a modifier.
     *
     * When using `Shift+`, make sure you also use the upper case letter/symbol.
     *
     * `ControlOrMeta` can be used to trigger Meta on Mac and control on others.
     *
     * `Space` and `Plus` can be used to imply the ` ` and `+` characters.
     */
    const addHotKey = (
        sequence: string,
        handler: (event: KeyboardEvent) => void,
        explanation?: string
    ) => {
        onMounted(() => {
            // If `ControlOrMeta` is used, add both.
            if (sequence.includes("ControlOrMeta")) {
                if (isMac) {
                    sequence = sequence.replaceAll("ControlOrMeta", "Meta");
                } else {
                    sequence = sequence.replaceAll("ControlOrMeta", "Control");
                }
            }

            // Consistently order the modifiers.
            sequence = sequence
                .split(" ")
                .map((combination) => {
                    return combination
                        .split("+")
                        .toSorted((a, b) => {
                            const aModifier = MODIFIERS.indexOf(a);
                            const bModifier = MODIFIERS.indexOf(b);
                            if (aModifier >= 0 && bModifier >= 0) {
                                return aModifier - bModifier;
                            } else if (aModifier >= 0) {
                                return -1;
                            } else if (bModifier >= 0) {
                                return 1;
                            } else {
                                return a.localeCompare(b);
                            }
                        })
                        .join("+");
                })
                .join(" ");

            if (hotKeys.value[sequence]) {
                console.debug(`Hotkey already registered: "${sequence}"`);
                return;
            }

            hotKeys.value[sequence] = {
                handler,
                explanation,
            };

            onUnmounted(() => {
                delete hotKeys.value[sequence];
            });
        });
    };

    /**
     * Attach the event listener for hotkeys.
     *
     * Generally this shold only happen once and be attached to the document.body.
     */
    const attach = (element: EventTarget = document.body) => {
        const sequence = ref<string>("");
        const hotKeySequences = computed(() => Object.keys(hotKeys.value));
        const timer = ref<number | NodeJS.Timeout>();

        const startTimer = () => {
            timer.value = setTimeout(reset, TIMEOUT);
        };

        const reset = () => {
            clearTimeout(timer.value);
            sequence.value = "";
        };

        useEventListener(element, "keydown", (event: KeyboardEvent) => {
            if (event.isComposing || event.repeat) {
                return;
            }

            if (MODIFIERS.includes(event.key)) {
                return;
            }

            if (!sequence.value) {
                startTimer();
            }

            let key = event.key;

            if (event.altKey) {
                sequence.value += "Alt+";
                if (isMac) {
                    // MacOS sends symbols when Alt is pressed.
                    key = MACOS_SYMBOL_LAYER_KEYS[key] ?? key;
                }
            }

            /**
             * The order of these modifiers is important since is must match
             * with the order in `addHotKey`.
             */
            if (event.ctrlKey) {
                sequence.value += "Control+";
            }

            if (event.metaKey) {
                sequence.value += "Meta+";
            }

            if (event.shiftKey) {
                sequence.value += "Shift+";
                if (isMac) {
                    // MacOS sends lowercase letters when command and shift are pressed.
                    // So we try to make them uppercase again.
                    key = MACOS_UPPERCASE_LAYER_KEYS[key] ?? key;
                }
            }

            // If no modifier is used, skip the typing as well.
            if (!(event.ctrlKey || event.metaKey)) {
                if (isTypable(event.target as HTMLElement)) {
                    return;
                }
            }

            key = SYNTHETIC_KEYNAMES[key] ?? key;

            sequence.value += `${key} `;

            // Check for a hotkey.
            if (hotKeySequences.value.includes(sequence.value.trim())) {
                hotKeys.value[sequence.value.trim()].handler(event);
                reset();
            }
        });
    };

    return { attach, addHotKey, hotKeys: readonly(hotKeys) };
};
